import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBusinessPlan } from "../App";
import { useUserEntitlements } from "../hooks/useUserEntitlements";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Settings, TrendingUp, AlertCircle, CheckCircle, DollarSign, Percent, Plus, Edit, Trash2, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Setup = () => {
  const { t } = useTranslation(['setup', 'common', 'errors', 'notifications']);
  const { currentPlan, activeScenario, setActiveScenario } = useBusinessPlan();
  const { canCreate, entitlements } = useUserEntitlements();
  const [setup, setSetup] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Scenario management state
  const [showCreateScenario, setShowCreateScenario] = useState(false);
  const [showEditScenario, setShowEditScenario] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [scenarioForm, setScenarioForm] = useState({
    name: '',
    volume_multiplier: 1.0,
    price_multiplier: 1.0,
    opex_multiplier: 1.0,
    working_capital_multiplier: 1.0
  });

  useEffect(() => {
    if (currentPlan) {
      fetchSetupData();
      fetchScenarios();
    }
  }, [currentPlan]);

  const fetchSetupData = async () => {
    try {
      const response = await axios.get(`${API}/setup/${currentPlan.id}`);
      setSetup(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching setup data:", error);
      setLoading(false);
      // Set default setup if fetch fails
      setSetup({
        vat_rate: 0.15,
        tax_rate: 0.30,
        discount_rate: 0.18,
        inflation_rate: 0.10,
        active_scenario: "Base",
        planning_season_start: null,
        planning_season_end: null
      });
    }
  };

  const fetchScenarios = async () => {
    try {
      const response = await axios.get(`${API}/scenarios/${currentPlan.id}`);
      setScenarios(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching scenarios:", error);
      setLoading(false);
    }
  };

  const handleSetupChange = (field, value) => {
    setSetup(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveSetup = async () => {
    if (!setup) {
      toast.error(t('setup:messages.setupNotLoaded'));
      return;
    }

    try {
      setSaving(true);
      const setupData = {
        plan_id: currentPlan.id,
        vat_rate: setup.vat_rate,
        tax_rate: setup.tax_rate,
        discount_rate: setup.discount_rate,
        inflation_rate: setup.inflation_rate,
        active_scenario: setup.active_scenario,
        planning_season_start: setup.planning_season_start,
        planning_season_end: setup.planning_season_end
      };

      await axios.put(`${API}/setup/${currentPlan.id}`, setupData);
      toast.success(t('setup:messages.setupSaved'));
    } catch (error) {
      console.error("Error saving setup:", error);
      toast.error(t('setup:messages.setupError'));
    } finally {
      setSaving(false);
    }
  };

  // Scenario management functions
  const createScenario = async () => {
    try {
      if (!scenarioForm.name.trim()) {
        toast.error(t('errors:validation.required'));
        return;
      }

      const scenarioData = {
        plan_id: currentPlan.id,
        name: scenarioForm.name.trim(),
        volume_multiplier: parseFloat(scenarioForm.volume_multiplier),
        price_multiplier: parseFloat(scenarioForm.price_multiplier),
        opex_multiplier: parseFloat(scenarioForm.opex_multiplier),
        working_capital_multiplier: parseFloat(scenarioForm.working_capital_multiplier)
      };

      await axios.post(`${API}/scenarios`, scenarioData);
      toast.success(t('setup:messages.scenarioCreated'));
      
      // Reset form and close dialog
      resetScenarioForm();
      setShowCreateScenario(false);
      
      // Refresh scenarios
      await fetchScenarios();
    } catch (error) {
      console.error("Error creating scenario:", error);
      toast.error(t('setup:messages.scenarioError'));
    }
  };

  const updateScenario = async () => {
    try {
      if (!scenarioForm.name.trim()) {
        toast.error(t('errors:validation.required'));
        return;
      }

      const scenarioData = {
        name: scenarioForm.name.trim(),
        plan_id: currentPlan.id,  // Add plan_id for consistency
        volume_multiplier: parseFloat(scenarioForm.volume_multiplier),
        price_multiplier: parseFloat(scenarioForm.price_multiplier),
        opex_multiplier: parseFloat(scenarioForm.opex_multiplier),
        working_capital_multiplier: parseFloat(scenarioForm.working_capital_multiplier)
      };

      await axios.put(`${API}/scenarios/${editingScenario.id}`, scenarioData);
      toast.success(t('setup:messages.scenarioUpdated'));
      
      // Reset form and close dialog
      resetScenarioForm();
      setShowEditScenario(false);
      setEditingScenario(null);
      
      // Refresh scenarios
      await fetchScenarios();
    } catch (error) {
      console.error("Error updating scenario:", error);
      toast.error(t('setup:messages.scenarioError'));
    }
  };

  const deleteScenario = async (scenarioId, scenarioName) => {
    if (!window.confirm(`Are you sure you want to delete the "${scenarioName}" scenario?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/scenarios/${scenarioId}`);
      toast.success(t('setup:messages.scenarioDeleted'));
      
      // Refresh scenarios
      await fetchScenarios();
    } catch (error) {
      console.error("Error deleting scenario:", error);
      toast.error(t('setup:messages.scenarioError'));
    }
  };

  const openEditScenario = (scenario) => {
    setEditingScenario(scenario);
    setScenarioForm({
      name: scenario.name,
      volume_multiplier: scenario.volume_multiplier,
      price_multiplier: scenario.price_multiplier,
      opex_multiplier: scenario.opex_multiplier,
      working_capital_multiplier: scenario.working_capital_multiplier
    });
    setShowEditScenario(true);
  };

  const resetScenarioForm = () => {
    setScenarioForm({
      name: '',
      volume_multiplier: 1.0,
      price_multiplier: 1.0,
      opex_multiplier: 1.0,
      working_capital_multiplier: 1.0
    });
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const ScenarioCard = ({ scenario, isActive }) => (
    <Card className={`glass-card shadow-soft transition-all ${isActive ? 'ring-2 ring-blue-500' : ''}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{scenario.name} Case</CardTitle>
          <div className="flex items-center space-x-2">
            {isActive && <Badge className="bg-blue-100 text-blue-700">Active</Badge>}
            <div className="flex space-x-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEditScenario(scenario)}
                className="h-7 w-7 p-0"
              >
                <Edit className="h-3 w-3" />
              </Button>
              {scenario.name !== 'Base' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => deleteScenario(scenario.id, scenario.name)}
                  className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        </div>
        <CardDescription>
          {scenario.name === 'Base' && 'Realistic baseline projections'}
          {scenario.name === 'Best' && 'Optimistic growth scenario'}
          {scenario.name === 'Worst' && 'Conservative downside scenario'}
          {!['Base', 'Best', 'Worst'].includes(scenario.name) && 'Custom business scenario'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Volume</span>
              <span className="font-medium">{formatPercentage(scenario.volume_multiplier - 1)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Price</span>
              <span className="font-medium">{formatPercentage(scenario.price_multiplier - 1)}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">OpEx</span>
              <span className="font-medium">{formatPercentage(scenario.opex_multiplier - 1)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Working Capital</span>
              <span className="font-medium">{formatPercentage(scenario.working_capital_multiplier - 1)}</span>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Button
            onClick={() => setActiveScenario(scenario.name)}
            variant={isActive ? "default" : "outline"}
            className={isActive ? "btn-primary w-full" : "btn-secondary w-full"}
          >
            {isActive ? "Currently Active" : "Switch to this Scenario"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-96 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Guard clause: If setup is null, show loading or initialization message
  if (!setup) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="p-8 text-center">
            <Settings className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Initializing Setup</h3>
            <p className="text-slate-600 mb-4">
              Loading configuration data...
            </p>
            <Button onClick={fetchSetupData}>
              Retry Loading
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8 fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          {t('setup:title')}
        </h1>
        <p className="text-slate-600">
          {t('setup:subtitle')} {currentPlan?.company_name}
        </p>
      </div>

      <Tabs defaultValue="setup" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="setup">{t('setup:tabs.basicSetup')}</TabsTrigger>
          <TabsTrigger value="scenarios">{t('setup:tabs.scenarioPlanning')}</TabsTrigger>
        </TabsList>

        {/* Basic Setup Tab */}
        <TabsContent value="setup" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Configuration Card */}
            <Card className="glass-card shadow-soft">
              <CardHeader>
                <div className="flex items-center">
                  <Settings className="h-5 w-5 mr-2 text-blue-600" />
                  <CardTitle>{t('setup:financialParameters.title')}</CardTitle>
                </div>
                <CardDescription>
                  {t('setup:financialParameters.subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {setup && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Label htmlFor="vat-rate" className="flex items-center gap-2 cursor-help">
                                {t('setup:financialParameters.vatRate')}
                                <HelpCircle className="h-4 w-4 text-gray-400" />
                              </Label>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <p className="font-medium mb-1">{t('setup:financialParameters.vatTooltip')}</p>
                              <p className="text-sm text-gray-600 mb-2">
                                {t('setup:financialParameters.vatDescription')}
                              </p>
                              <div className="text-xs text-gray-500 space-y-1">
                                <p><strong>{t('setup:financialParameters.vatRange')}</strong></p>
                                <p><strong>{t('setup:financialParameters.vatExamples')}</strong></p>
                                <p>• Ethiopia: 15%</p>
                                <p>• UK/EU: 20%</p>
                                <p>• UAE: 5%</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <div className="relative">
                          <Input
                            id="vat-rate"
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={(setup.vat_rate * 100).toFixed(1)}
                            onChange={(e) => handleSetupChange('vat_rate', parseFloat(e.target.value) / 100)}
                            className="form-input pr-8"
                          />
                          <Percent className="absolute right-3 top-3 h-4 w-4 text-slate-400" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Label htmlFor="tax-rate" className="flex items-center gap-2 cursor-help">
                                Corporate Tax Rate (%)
                                <HelpCircle className="h-4 w-4 text-gray-400" />
                              </Label>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <p className="font-medium mb-1">Corporate Income Tax</p>
                              <p className="text-sm text-gray-600 mb-2">
                                The percentage of corporate profits paid as tax to the government.
                              </p>
                              <div className="text-xs text-gray-500 space-y-1">
                                <p><strong>Typical range:</strong> 15% - 35%</p>
                                <p><strong>Examples:</strong></p>
                                <p>• Ethiopia: 30%</p>
                                <p>• USA: 21%</p>
                                <p>• UK: 25%</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <div className="relative">
                          <Input
                            id="tax-rate"
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={(setup.tax_rate * 100).toFixed(1)}
                            onChange={(e) => handleSetupChange('tax_rate', parseFloat(e.target.value) / 100)}
                            className="form-input pr-8"
                          />
                          <Percent className="absolute right-3 top-3 h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Label htmlFor="discount-rate" className="flex items-center gap-2 cursor-help">
                                Discount Rate (%)
                                <HelpCircle className="h-4 w-4 text-gray-400" />
                                <span className="text-xs text-slate-500 font-normal">for NPV/IRR</span>
                              </Label>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-sm">
                              <p className="font-medium mb-1">Discount Rate (WACC)</p>
                              <p className="text-sm text-gray-600 mb-2">
                                The rate used to calculate the present value of future cash flows. 
                                Also known as WACC (Weighted Average Cost of Capital). This determines your NPV and IRR calculations.
                              </p>
                              <div className="text-xs text-gray-500 space-y-1">
                                <p><strong>Typical range:</strong> 8% - 15%</p>
                                <p><strong>Guidelines:</strong></p>
                                <p>• Stable businesses: 8-10%</p>
                                <p>• Growing businesses: 10-12%</p>
                                <p>• High-growth startups: 12-15%</p>
                                <p>• Risky ventures: 15%+</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <div className="relative">
                          <Input
                            id="discount-rate"
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={(setup.discount_rate * 100).toFixed(1)}
                            onChange={(e) => handleSetupChange('discount_rate', parseFloat(e.target.value) / 100)}
                            className="form-input pr-8"
                          />
                          <Percent className="absolute right-3 top-3 h-4 w-4 text-slate-400" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Label htmlFor="inflation-rate" className="flex items-center gap-2 cursor-help">
                                Inflation Rate (%)
                                <HelpCircle className="h-4 w-4 text-gray-400" />
                              </Label>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <p className="font-medium mb-1">Expected Annual Inflation</p>
                              <p className="text-sm text-gray-600 mb-2">
                                The expected annual increase in general price levels. 
                                This affects cost escalations and price adjustments over time.
                              </p>
                              <div className="text-xs text-gray-500 space-y-1">
                                <p><strong>Typical range:</strong> 2% - 10%</p>
                                <p><strong>Examples:</strong></p>
                                <p>• Stable economies: 2-3%</p>
                                <p>• Emerging markets: 5-8%</p>
                                <p>• High inflation: 10%+</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <div className="relative">
                          <Input
                            id="inflation-rate"
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={(setup.inflation_rate * 100).toFixed(1)}
                            onChange={(e) => handleSetupChange('inflation_rate', parseFloat(e.target.value) / 100)}
                            className="form-input pr-8"
                          />
                          <Percent className="absolute right-3 top-3 h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    </div>

                    {/* Planning Season Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                      <div className="space-y-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Label htmlFor="planning-season-start" className="flex items-center cursor-help">
                                Planning Season Start
                                <HelpCircle className="h-3 w-3 ml-1 text-slate-400" />
                              </Label>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>Start date of your business planning period. Ethiopian fiscal year typically starts July 7 (Hamle 1). Example: 2025-07-07</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Input
                          id="planning-season-start"
                          type="date"
                          value={setup.planning_season_start || ''}
                          onChange={(e) => handleSetupChange('planning_season_start', e.target.value)}
                          className="form-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Label htmlFor="planning-season-end" className="flex items-center cursor-help">
                                Planning Season End
                                <HelpCircle className="h-3 w-3 ml-1 text-slate-400" />
                              </Label>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>End date of your business planning period. Ethiopian fiscal year typically ends July 6 (Sene 30). Example: 2026-07-06</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Input
                          id="planning-season-end"
                          type="date"
                          value={setup.planning_season_end || ''}
                          onChange={(e) => handleSetupChange('planning_season_end', e.target.value)}
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="active-scenario">Active Scenario</Label>
                      <Select 
                        value={setup.active_scenario} 
                        onValueChange={(value) => {
                          handleSetupChange('active_scenario', value);
                          setActiveScenario(value);
                        }}
                      >
                        <SelectTrigger className="form-select">
                          <SelectValue placeholder="Select scenario" />
                        </SelectTrigger>
                        <SelectContent>
                          {scenarios.map((scenario) => (
                            <SelectItem key={scenario.id} value={scenario.name}>
                              {scenario.name} Case
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={saveSetup}
                      disabled={saving}
                      className="btn-primary w-full"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {t('setup:actions.saving')}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {t('setup:actions.saveConfiguration')}
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Business Information Card */}
            <Card className="glass-card shadow-soft">
              <CardHeader>
                <div className="flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                  <CardTitle>Business Information</CardTitle>
                </div>
                <CardDescription>
                  Current business plan configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">Company Name</span>
                    <span className="font-medium">{currentPlan?.company_name}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">Plan Name</span>
                    <span className="font-medium">{currentPlan?.name}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">Currency</span>
                    <span className="font-medium">{currentPlan?.currency}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">Fiscal Year Start</span>
                    <span className="font-medium">
                      {new Date(2024, currentPlan?.fiscal_year_start - 1, 1).toLocaleDateString('en-US', { month: 'long' })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-slate-600">Planning Horizon</span>
                    <span className="font-medium">
                      {setup?.planning_season_start && setup?.planning_season_end ? (
                        `${new Date(setup.planning_season_start).getFullYear()} - ${new Date(setup.planning_season_end).getFullYear()}`
                      ) : (
                        'Not set'
                      )}
                    </span>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900 mb-1">Configuration Tips</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• VAT rates typically range from 5-25%</li>
                        <li>• Corporate tax rates vary by jurisdiction</li>
                        <li>• Discount rates reflect cost of capital</li>
                        <li>• Inflation affects price escalations</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Scenario Planning Tab */}
        <TabsContent value="scenarios" className="space-y-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
                <h2 className="text-xl font-semibold">Scenario Analysis</h2>
              </div>
              <Button 
                onClick={() => {
                  // Check if Starter tier at limit
                  const userTier = entitlements?.tier?.toLowerCase();
                  if (userTier === 'starter' && !canCreate('scenarios')) {
                    // Show CTA
                    toast.error(
                      <div className="space-y-2">
                        <p className="font-bold">Scenario Limit Reached</p>
                        <p className="text-sm">Upgrade to Professional or buy an add-on to create more scenarios.</p>
                        <div className="flex gap-2 mt-2">
                          <button 
                            onClick={() => window.location.href = '/subscription'}
                            className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                          >
                            View Subscription
                          </button>
                          <button 
                            onClick={() => window.location.href = '/add-ons'}
                            className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                          >
                            Browse Add-ons
                          </button>
                        </div>
                      </div>,
                      { duration: 10000 }
                    );
                  } else {
                    setShowCreateScenario(true);
                  }
                }}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Scenario
              </Button>
            </div>
            <p className="text-slate-600">
              Compare different business scenarios with varying assumptions. The active scenario affects all financial calculations.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {scenarios.map((scenario) => (
              <ScenarioCard
                key={scenario.id}
                scenario={scenario}
                isActive={activeScenario === scenario.name}
              />
            ))}
          </div>

          {/* Scenario Comparison Chart */}
          <Card className="glass-card shadow-soft">
            <CardHeader>
              <CardTitle>Scenario Impact Analysis</CardTitle>
              <CardDescription>
                Understand how different scenarios affect key business drivers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {['Volume', 'Price', 'OpEx', 'Working Capital'].map((metric) => (
                  <div key={metric} className="space-y-3">
                    <h4 className="font-medium text-slate-700">{metric} Impact</h4>
                    <div className="space-y-2">
                      {scenarios.map((scenario) => {
                        const getMultiplier = () => {
                          switch (metric) {
                            case 'Volume': return scenario.volume_multiplier;
                            case 'Price': return scenario.price_multiplier;
                            case 'OpEx': return scenario.opex_multiplier;
                            case 'Working Capital': return scenario.working_capital_multiplier;
                            default: return 1;
                          }
                        };
                        const multiplier = getMultiplier();
                        const impact = (multiplier - 1) * 100;
                        const isPositive = impact > 0;
                        const isNeutral = impact === 0;
                        
                        return (
                          <div key={scenario.name} className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">{scenario.name}</span>
                            <Badge 
                              variant={isNeutral ? "secondary" : isPositive ? "default" : "destructive"}
                              className={
                                isNeutral ? "bg-slate-100 text-slate-700" :
                                isPositive ? "bg-green-100 text-green-700" :
                                "bg-red-100 text-red-700"
                              }
                            >
                              {impact > 0 ? '+' : ''}{impact.toFixed(1)}%
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Hero Image */}
          <Card className="glass-card shadow-soft">
            <CardContent className="p-0">
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxkYXNoYm9hcmQlMjBhbmFseXRpY3N8ZW58MHx8fHwxNzU4NTQzNTE0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Strategic Planning"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-blue-900/80"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white">
                    <h3 className="text-2xl font-bold mb-2">Strategic Scenario Planning</h3>
                    <p className="text-purple-100">Model multiple futures to make better decisions</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Scenario Dialog */}
      <Dialog open={showCreateScenario} onOpenChange={setShowCreateScenario}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Scenario</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label htmlFor="scenario-name">Scenario Name *</Label>
              <Input
                id="scenario-name"
                value={scenarioForm.name}
                onChange={(e) => setScenarioForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Expansion Plan, Economic Downturn, New Market Entry"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="volume-multiplier">Volume Multiplier</Label>
                <Input
                  id="volume-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.volume_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, volume_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
                <p className="text-xs text-slate-500 mt-1">1.0 = no change, 1.2 = +20% volume</p>
              </div>

              <div>
                <Label htmlFor="price-multiplier">Price Multiplier</Label>
                <Input
                  id="price-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.price_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, price_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
                <p className="text-xs text-slate-500 mt-1">1.0 = no change, 1.1 = +10% price</p>
              </div>

              <div>
                <Label htmlFor="opex-multiplier">OpEx Multiplier</Label>
                <Input
                  id="opex-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.opex_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, opex_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
                <p className="text-xs text-slate-500 mt-1">1.0 = no change, 0.9 = -10% OpEx</p>
              </div>

              <div>
                <Label htmlFor="wc-multiplier">Working Capital Multiplier</Label>
                <Input
                  id="wc-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.working_capital_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, working_capital_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
                <p className="text-xs text-slate-500 mt-1">1.0 = no change, 1.15 = +15% WC</p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">Scenario Tips</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Volume affects sales quantities/units</li>
                    <li>• Price impacts revenue per unit/service</li>
                    <li>• OpEx affects operating expenses</li>
                    <li>• Working Capital affects cash flow timing</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateScenario(false);
                  resetScenarioForm();
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={createScenario}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Create Scenario
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Scenario Dialog */}
      <Dialog open={showEditScenario} onOpenChange={setShowEditScenario}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Scenario</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label htmlFor="edit-scenario-name">Scenario Name *</Label>
              <Input
                id="edit-scenario-name"
                value={scenarioForm.name}
                onChange={(e) => setScenarioForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Expansion Plan, Economic Downturn"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-volume-multiplier">Volume Multiplier</Label>
                <Input
                  id="edit-volume-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.volume_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, volume_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
              </div>

              <div>
                <Label htmlFor="edit-price-multiplier">Price Multiplier</Label>
                <Input
                  id="edit-price-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.price_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, price_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
              </div>

              <div>
                <Label htmlFor="edit-opex-multiplier">OpEx Multiplier</Label>
                <Input
                  id="edit-opex-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.opex_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, opex_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
              </div>

              <div>
                <Label htmlFor="edit-wc-multiplier">Working Capital Multiplier</Label>
                <Input
                  id="edit-wc-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.working_capital_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, working_capital_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditScenario(false);
                  setEditingScenario(null);
                  resetScenarioForm();
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={updateScenario}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Update Scenario
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Setup;